import type { DoneInvokeEvent } from 'xstate';
import { assign, createMachine } from 'xstate';
import type { SubscriptionInfo } from '~/subscription-management/api/subscription/model';
import { getSubscriptionInfo } from '~/subscription-management/api/subscription/get-subscription-info';
import { subscriptionApi } from '../api';
import type {
  Context,
  Event,
  SetPaymentMethodEvent,
  ToggleEditEvent,
} from './payment-details-page-machine.types';
import {
  ELEMENT_NAME,
  FORM_NAME,
  PAGE,
  trackFormSubmit,
  trackViewElement,
} from '../utils/tracking';

export const paymentDetailsPageMachine = createMachine(
  {
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports
    tsTypes: {} as import('./payment-details-page.machine.typegen').Typegen0,
    schema: {
      context: {} as Context,
      events: {} as Event,
      services: {} as {
        getSubscriptionInfo: { data: SubscriptionInfo };
      },
    },
    initial: 'initialise',
    id: 'payment-details-page',
    states: {
      initialise: {
        id: 'initialise',
        exit: (context) => {
          trackViewElement({
            name: context.customer ? ELEMENT_NAME.paymentDetails : ELEMENT_NAME.paymentDetailsEmpty,
            accountId: context.accountId,
            uiPosition: PAGE.payment,
          });
        },
        invoke: {
          src: 'getSubscriptionInfo',
          id: 'getSubscriptionInfo',
          onDone: [
            {
              cond: 'isExternalPaymentCustomer',
              actions: assign<Context, DoneInvokeEvent<SubscriptionInfo>>((_context, event) => ({
                accountId: event.data.user.accountId,
                customer: event.data.customer,
              })),
              target: 'noPaymentMethod',
            },
            {
              cond: 'customerExists',
              actions: assign<Context, DoneInvokeEvent<SubscriptionInfo>>((_context, event) => ({
                paymentMethod: event.data.customer?.paymentMethod,
                accountId: event.data.user.accountId,
                customer: event.data.customer,
                isOverdue: event.data.customer?.subscription.status === 'OVERDUE',
              })),
              target: 'viewPaymentMethod',
            },
            {
              actions: assign<Context, DoneInvokeEvent<SubscriptionInfo>>((_context, event) => ({
                accountId: event.data.user.accountId,
              })),
              target: 'noPaymentMethod',
            },
          ],
          onError: [
            {
              target: 'noPaymentMethod',
            },
          ],
        },
      },
      noPaymentMethod: {},
      viewPaymentMethod: {
        id: 'viewPaymentMethod',
        on: {
          TOGGLE_EDIT: {
            target: 'editPaymentMethod',
          },
        },
      },
      editPaymentMethod: {
        initial: 'editing',
        states: {
          editing: {
            on: {
              SET_PAYMENT_METHOD: {
                target: 'processing',
                actions: assign<Context, SetPaymentMethodEvent>({
                  paymentMethodId: (_context, event) => event.paymentMethodId,
                }),
              },
              TOGGLE_EDIT: {
                target: '#viewPaymentMethod',
                actions: assign<Context, ToggleEditEvent>(() => ({ error: undefined })),
              },
            },
          },
          processing: {
            invoke: {
              id: 'updatePaymentMethodService',
              src: 'updatePaymentMethodService',
              onDone: [
                {
                  target: '#initialise',
                  actions: assign<Context, DoneInvokeEvent<Event>>(() => ({ error: undefined })),
                },
              ],
              onError: [
                {
                  target: 'editing',
                  actions: [
                    assign<Context, DoneInvokeEvent<Event>>(() => ({ error: 'failed_submission' })),
                    (context) => {
                      trackFormSubmit({
                        name: FORM_NAME.updatePayment,
                        submitSuccess: false,
                        accountId: context.accountId,
                        errorMessage: 'Failed to update payment method',
                      });
                    },
                  ],
                },
              ],
            },
          },
        },
      },
    },
  },
  {
    services: {
      getSubscriptionInfo,
      updatePaymentMethodService: (context) => {
        return subscriptionApi.updatePaymentMethod({
          paymentMethodId: context.paymentMethodId,
          hotjarAccountId: context.accountId,
        });
      },
    },
    guards: {
      customerExists: (_context, event: DoneInvokeEvent<SubscriptionInfo>) =>
        event.data.customer !== null,
      isExternalPaymentCustomer: (_context, event: DoneInvokeEvent<SubscriptionInfo>) =>
        event.data.customer?.paymentMethod.paymentMethodType === 'EXTERNAL_PAYMENT_METHOD',
    },
  },
);
